<template>
  <div class="notification-bell position-relative">
    <button
      class="btn btn-link position-relative"
      @click="toggleDropdown"
      :aria-expanded="showDropdown"
      aria-haspopup="true"
    >
      <font-awesome-icon icon="bell" size="lg" />
      <span
        v-if="unreadCount > 0"
        class="badge bg-danger position-absolute top-0 start-100 translate-middle"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Dropdown -->
    <Teleport to="#modalTarget">
      <div v-if="showDropdown" class="notification-dropdown" @click.stop>
      <div class="notification-header">
        <h6 class="mb-0">Benachrichtigungen</h6>
      </div>

      <div class="notification-list">
        <div v-if="loading" class="text-center p-3">
          <font-awesome-icon icon="spinner" spin />
          <p class="mb-0 mt-2">Lade Benachrichtigungen...</p>
        </div>

        <div v-else-if="notifications.length === 0" class="text-center p-3 text-muted">
          <font-awesome-icon icon="bell-slash" size="2x" />
          <p class="mb-0 mt-2">Keine Benachrichtigungen</p>
        </div>

        <div v-else>
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            :class="{ unread: !notification.readAt }"
            @click="handleNotificationClick(notification)"
          >
            <div class="notification-content">
              <h6 class="notification-title">{{ notification.title }}</h6>
              <p class="notification-message">{{ notification.message }}</p>
              <small class="notification-time">
                {{ formatTime(notification.createdAt) }}
              </small>
            </div>
            <div v-if="!notification.readAt" class="notification-indicator"></div>
          </div>
        </div>
      </div>

      <div v-if="notifications.length > 0" class="notification-footer">
        <router-link
          to="/notifications"
          class="btn btn-sm btn-outline-secondary w-100"
          @click="showDropdown = false"
        >
          Alle Benachrichtigungen ansehen
        </router-link>
      </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/stores/notifications'
import notificationService from '@/services/notificationService'

export default {
  name: 'NotificationBell',
  setup() {
    const router = useRouter()
    const notificationStore = useNotificationStore()
    const showDropdown = ref(false)
    const markingAllAsRead = ref(false)

    const notifications = computed(() => notificationStore.notifications)
    const loading = computed(() => notificationStore.loading)
    const unreadCount = computed(() => {
      return notificationStore.notifications.filter((n) => !n.readAt).length
    })

    const loadNotifications = async () => {
      try {
        await notificationStore.fetchNotifications({
          limit: 10,
          unreadOnly: false,
        })
      } catch (error) {
        console.error('Fehler beim Laden der Benachrichtigungen:', error)
      }
    }

    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value
      if (showDropdown.value && notifications.value.length === 0) {
        loadNotifications()
      }
    }

    const markAsRead = async (notificationId) => {
      try {
        await notificationStore.markAsRead(notificationId)
      } catch (error) {
        console.error('Fehler beim Markieren als gelesen:', error)
      }
    }

    const markAllAsRead = async () => {
      try {
        markingAllAsRead.value = true
        await notificationStore.markAllAsRead()
      } catch (error) {
        console.error('Fehler beim Markieren aller als gelesen:', error)
      } finally {
        markingAllAsRead.value = false
      }
    }

    const handleNotificationClick = (notification) => {
      notificationService.handleNotificationClick(
        notification, 
        markAsRead, 
        router, 
        () => showDropdown.value = false
      )
    }

    // Verwende Service-Funktionen
    const { getNotificationLink, formatTime } = notificationService


    const handleClickOutside = (event) => {
      if (showDropdown.value && !event.target.closest('.notification-bell')) {
        showDropdown.value = false
      }
    }


    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      showDropdown,
      notifications,
      loading,
      markingAllAsRead,
      unreadCount,
      toggleDropdown,
      markAsRead,
      markAllAsRead,
      handleNotificationClick,
      formatTime,
    }
  },
}
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: flex;
  align-items: center;
}

.notification-bell .btn-link {
  color: white !important;
  text-decoration: none;
  padding: 0.375rem 0.5rem;
  border: none;
  background: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.notification-bell .btn-link:hover {
  color: #e2e8f0 !important;
}

.notification-dropdown {
  position: fixed;
  top: 60px;
  right: 20px;
  width: 350px;
  max-height: 500px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 1050;
  overflow: hidden;
}

.notification-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
}

.notification-list {
  max-height: 300px;
  overflow-y: auto;
}

.notification-item {
  padding: 1rem;
  border-bottom: 1px solid #f1f3f4;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  transition: background-color 0.2s;
  position: relative;
}

.notification-item:hover {
  background-color: #f8f9fa;
}

.notification-item.unread {
  background-color: #e7f3ff;
  border-left: 3px solid #4a86e8;
}

.notification-content {
  flex: 1;
}

.notification-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: #1e293b;
}

.notification-message {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
}

.notification-time {
  color: #9ca3af;
  font-size: 0.75rem;
}

.notification-indicator {
  width: 8px;
  height: 8px;
  background-color: #4a86e8;
  border-radius: 50%;
  margin-top: 0.5rem;
  flex-shrink: 0;
}

.notification-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

.badge {
  font-size: 0.7rem;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .notification-dropdown {
    width: 300px;
    right: 10px;
    top: 70px;
  }
}
</style>
